import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Wind, Flame, HandHeart, Clock, ChevronRight, BookOpen } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";

const CATEGORY_META = {
  spirit: { Icon: Wind, label_en: "Holy Spirit", label_es: "Espíritu Santo" },
  life: { Icon: HandHeart, label_en: "Christian Life", label_es: "Vida cristiana" },
  power: { Icon: Flame, label_en: "Power & Warfare", label_es: "Poder y guerra" },
  hope: { Icon: Clock, label_en: "Hope & End Times", label_es: "Esperanza y últimos tiempos" },
};

export default function Studies() {
  const { lang } = useApp();
  const [topics, setTopics] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const res = await api.get("/study/topics");
      setTopics(res.data);
    })();
  }, []);

  const openTopic = async (t) => {
    setSelected(t);
    setData(null);
    setLoading(true);
    try {
      const res = await api.get(`/study/topics/${t.topic_id}`, { params: { lang } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  const grouped = topics.reduce((acc, t) => {
    (acc[t.category] ||= []).push(t);
    return acc;
  }, {});

  const T = (en, es) => (lang === "es" ? es : en);

  const goRef = (ref) => {
    const m = ref.match(/^(.+?)\s+(\d+):(\d+)/);
    if (!m) return;
    // We can't easily resolve book name → id here without hitting an API.
    // Instead, pass query params by name; Reader resolves by name from its books list.
    const name = encodeURIComponent(m[1].trim());
    navigate(`/read?ref_name=${name}&c=${m[2]}&v=${m[3]}`);
  };

  return (
    <div>
      <div className="flex items-center gap-2 text-[var(--accent)]">
        <Sparkles className="w-4 h-4" strokeWidth={1.5} />
        <span className="uppercase tracking-[0.3em] text-[10px]">
          {T("Esencia", "Esencia")}
        </span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
        {T("Special studies", "Estudios especiales")}
      </h1>
      <p className="mt-3 text-[var(--text-secondary)] text-sm max-w-2xl leading-relaxed">
        {T(
          "Original thematic articles. Not affiliated with any published study Bible.",
          "Artículos temáticos originales. No están afiliados con ninguna biblia de estudio publicada."
        )}
      </p>

      <div className="mt-10 space-y-10">
        {Object.entries(grouped).map(([cat, items]) => {
          const meta = CATEGORY_META[cat] || {};
          const Icon = meta.Icon;
          return (
            <section key={cat}>
              <div className="flex items-center gap-2 mb-4">
                {Icon && <Icon className="w-4 h-4 text-[var(--accent)]" strokeWidth={1.5} />}
                <p className="uppercase tracking-[0.3em] text-[10px] text-[var(--text-secondary)]">
                  {lang === "es" ? meta.label_es : meta.label_en}
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cat === "power" && (
                  <button
                    key="book-liberacion-reino-dios"
                    data-testid="book-liberacion-reino-dios"
                    onClick={() => navigate("/studies/book/liberacion-reino-dios")}
                    className="group text-left rounded-2xl border border-[var(--accent)]/40 bg-gradient-to-br from-[var(--accent)]/10 to-[var(--surface-elevated)] p-5 hover:from-[var(--accent)]/15 transition-colors duration-200 md:col-span-2 lg:col-span-3"
                  >
                    <div className="flex items-center gap-2 text-[var(--accent)]">
                      <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                      <span className="uppercase tracking-[0.3em] text-[10px]">
                        {T("Book · FaithCycle", "Libro · FaithCycle")}
                      </span>
                    </div>
                    <div className="mt-2 flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-serif text-2xl md:text-3xl tracking-tight">
                          {T("Deliverance and the Kingdom of God", "La Liberación y El Reino de Dios")}
                        </h3>
                        <p className="mt-2 text-sm text-[var(--text-secondary)] leading-relaxed">
                          {T(
                            "Introduction, 12 chapters and conclusion. Free for everyone.",
                            "Introducción, 12 capítulos y conclusión. Gratis para todos."
                          )}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 mt-1 text-[var(--text-secondary)] group-hover:translate-x-1 transition-transform duration-200 flex-shrink-0" strokeWidth={1.5} />
                    </div>
                  </button>
                )}
                {items.map((t) => (
                  <button
                    key={t.topic_id}
                    data-testid={`topic-${t.topic_id}`}
                    onClick={() => openTopic(t)}
                    className="group text-left rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-5 hover:bg-[var(--surface)] transition-colors duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif text-xl tracking-tight">
                        {lang === "es" ? t.title_es : t.title_en}
                      </h3>
                      <ChevronRight className="w-4 h-4 text-[var(--text-secondary)] group-hover:translate-x-1 transition-transform duration-200" strokeWidth={1.5} />
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setData(null); } }}>
        <DialogContent className="bg-[var(--surface-elevated)] border-[var(--border)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="topic-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[var(--accent)]">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              <span className="uppercase tracking-[0.3em] text-[10px]">
                {T("Special study", "Estudio especial")}
              </span>
            </div>
            <DialogTitle className="font-serif text-2xl md:text-3xl tracking-tight">
              {selected ? (lang === "es" ? selected.title_es : selected.title_en) : ""}
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-4 w-3/4 bg-[var(--surface)]" />
              <Skeleton className="h-4 w-full bg-[var(--surface)]" />
              <Skeleton className="h-4 w-5/6 bg-[var(--surface)]" />
              <Skeleton className="h-4 w-2/3 bg-[var(--surface)]" />
            </div>
          )}

          {data && !loading && (
            <div className="space-y-6" data-testid="topic-content">
              {data.summary && (
                <p className="text-[var(--text-primary)] leading-relaxed font-serif-body text-lg">{data.summary}</p>
              )}

              {data.sections?.map((s, i) => (
                <div key={i}>
                  <h3 className="font-serif text-xl tracking-tight mb-2">{s.heading}</h3>
                  <p className="text-[var(--text-primary)] leading-relaxed">{s.body}</p>
                </div>
              ))}

              {data.key_passages?.length > 0 && (
                <div>
                  <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)] mb-2">
                    {T("Key passages", "Pasajes clave")}
                  </p>
                  <div className="space-y-2">
                    {data.key_passages.map((k, i) => (
                      <button
                        key={i}
                        data-testid={`topic-passage-${i}`}
                        onClick={() => goRef(k.ref)}
                        className="w-full text-left p-3 rounded-lg border border-[var(--border)] hover:bg-[var(--surface)] transition-colors duration-200 group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">{k.ref}</span>
                          <ChevronRight className="w-4 h-4 text-[var(--text-secondary)] group-hover:translate-x-1 transition-transform duration-200" strokeWidth={1.5} />
                        </div>
                        <p className="text-sm mt-1 text-[var(--text-primary)]">{k.note}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {data.practical_steps?.length > 0 && (
                <div>
                  <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)] mb-2">
                    {T("Practical steps", "Pasos prácticos")}
                  </p>
                  <ul className="space-y-2">
                    {data.practical_steps.map((s, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--accent)] flex-shrink-0"></span>
                        <span className="text-[var(--text-primary)] leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
